import {
  Badge,
  FlexBox,
  Icon,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { Presence } from 'lib-shared-legacy-types';
import moment, { Moment } from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';
import { useTimeoutFn } from 'react-use';

import { AwayStatus } from 'apis/presence';

import styles from './customAwayStatus.module.scss';

type CustomAwayStatusProps = {
  /** The custom away status */
  awayStatus: AwayStatus;
  /** When the status was updated */
  statusUpdatedAt: Presence['presence_status']['status_updated_at'];
  /** Whether to show the status label or not */
  showLabel?: boolean;
};

export const CustomAwayStatus = ({
  awayStatus,
  statusUpdatedAt,
  showLabel,
}: CustomAwayStatusProps) => {
  const { label, emoji, duration_limit_minutes } = awayStatus;

  const [limitExceeded, setLimitExceeded] = useState<boolean>(false);

  const durationLimitExceededAt: Moment | null = useMemo(() => {
    return statusUpdatedAt && duration_limit_minutes
      ? moment(new Date(statusUpdatedAt)).add(duration_limit_minutes, 'minutes')
      : null;
  }, [statusUpdatedAt, duration_limit_minutes]);

  const timeoutDuration = useMemo(
    () =>
      durationLimitExceededAt
        ? Math.max(durationLimitExceededAt.diff(moment()), 0)
        : 0,
    [durationLimitExceededAt],
  );
  const timeoutFunction = () => {
    if (durationLimitExceededAt != null) {
      setLimitExceeded(true);
    }
  };

  // Set up timer to set limit exceeded in the future
  const [isReady, cancel] = useTimeoutFn(timeoutFunction, timeoutDuration);

  useEffect(() => {
    // Check if the limit has already been exceeded
    const isLimitExceeded =
      durationLimitExceededAt != null
        ? durationLimitExceededAt.isBefore(moment())
        : false;
    if (isLimitExceeded && isReady() !== null) {
      cancel();
    }
    setLimitExceeded(isLimitExceeded);
  }, [durationLimitExceededAt, isReady, cancel]);

  const showLabelInToolTip = !showLabel;
  const tooltipContent = (
    <p className={styles.tooltipLabel}>
      {showLabelInToolTip && <span>{label}</span>}
      {statusUpdatedAt && (
        <>
          {showLabelInToolTip && <span> - </span>}
          {limitExceeded && durationLimitExceededAt ? (
            <>
              <span>limit exceeded&#32;</span>
              <Timestamp
                time={durationLimitExceededAt.toString()}
                relative
                initialFormat="counter"
              />
            </>
          ) : (
            <Timestamp
              time={statusUpdatedAt}
              relative
              counter
              isSecondsDisplayed
            />
          )}
        </>
      )}
    </p>
  );

  const content = (
    <Tooltip content={tooltipContent}>
      <FlexBox alignItems="center" gap="200">
        <span>{emoji}</span>
        {showLabel && <span>{label}</span>}
        {limitExceeded && (
          <FlexBox alignItems="center" className={styles.iconContainer}>
            <div className={styles.iconBackground} />
            <Icon
              name="exclamation-circle-filled"
              className={styles.icon}
              color={colors.red['600']}
              size={showLabel ? 16 : 20}
            />
          </FlexBox>
        )}
      </FlexBox>
    </Tooltip>
  );

  if (showLabel) {
    return (
      <Badge size="medium" variant={limitExceeded ? 'error' : 'warning'}>
        {content}
      </Badge>
    );
  }

  return content;
};
